import { Component } from '@theme/component';

/**
 * @typedef {Object} TabbedContentRefs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * A custom element that displays tabbed content panels.
 *
 * @extends {Component<TabbedContentRefs>}
 */
class TabbedContentComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }

    this.#syncTabLabels();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncTabLabels();
  }

  /**
   * Copies each panel's `data-tab-label` (rendered by the tab block, which has
   * reliable access to its own settings) onto the corresponding tab button.
   * The section-level label render is kept as the initial/fallback state.
   */
  #syncTabLabels() {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || !panels?.length) return;

    for (const [i, panel] of panels.entries()) {
      const label = panel.dataset.tabLabel?.trim();
      const tab = tabs[i];

      if (label && tab && tab.textContent.trim() !== label) {
        tab.textContent = label;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @returns {number} The index of the currently active tab */
  get #activeIndex() {
    return Math.max(
      0,
      this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
  }

  /**
   * Activates the tab and panel at the given index.
   *
   * @param {number} index - The index of the tab to activate.
   * @param {{ focus?: boolean }} [options] - Whether to move focus to the activated tab.
   */
  select(index, { focus = false } = {}) {
    const { tabs, panels } = this.refs;
    if (!tabs?.[index]) return;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      panels?.[i]?.toggleAttribute('hidden', !selected);
    }

    const tab = tabs[index];

    if (focus) {
      tab.focus();
    }

    tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const index = this.refs.tabs.indexOf(tab);
    if (index === -1) return;

    this.select(index);
  };

  /**
   * Handles keyboard navigation within the tab list.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[role="tablist"]')) return;

    const { tabs } = this.refs;
    const lastIndex = tabs.length - 1;
    let index = this.#activeIndex;

    switch (event.key) {
      case 'ArrowRight':
        index = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        index = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        index = 0;
        break;
      case 'End':
        index = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(index, { focus: true });
  };

  /**
   * Reveals the panel that contains the block selected in the theme editor.
   *
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.contains(event.target)) return;

    const panel = event.target.closest('[role="tabpanel"]');
    if (!(panel instanceof HTMLElement)) return;

    const index = this.refs.panels.indexOf(panel);
    if (index === -1 || index === this.#activeIndex) return;

    this.select(index);
  };
}

if (!customElements.get('tabbed-content-component')) {
  customElements.define('tabbed-content-component', TabbedContentComponent);
}
